import { Check, CircleX, Trash2 } from '@tamagui/lucide-icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@unistocks/components/button/button';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Loader } from '@unistocks/components/loading/spinner/loader';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { NoData } from '@unistocks/components/no-data/no-data';
import { Paragraph } from '@unistocks/components/text/text';
import { toast } from '@unistocks/components/toast';
import { invalidateNotificationsGetListQuery } from '@unistocks/features/notifications/invalidate-notification-list';
import { RelatedEntityComponentProps } from '@unistocks/features/notifications/related-entities/related-entity-component-props';
import { externalTransactionDate } from '@unistocks/features/portfolios/transactions/transaction-consts';
import { TransactionStatus } from '@unistocks/proxy/axios-client';
import {
  useNotificationsDeleteMutation
} from '@unistocks/proxy/axios-client/NotificationsQuery';
import {
  usePortfoliosGetQuery
} from '@unistocks/proxy/axios-client/PortfoliosQuery';
import {
  useTransactionsApproveMutation,
  useTransactionsGetQuery
} from '@unistocks/proxy/axios-client/TransactionsQuery';
import { formatCurrency, formatDate, t } from '@unistocks/shared/localization/index.ts';
import { transactionTypeOptions } from '@unistocks/shared/utils/enums/transaction-options';
import { memo } from 'react';

export const TransactionNotificationData = memo<RelatedEntityComponentProps>(
  ({ notificationId, relatedEntity: { id }, onComplete }) => {
    const queryClient = useQueryClient();
    const {
      data: transaction,
      isLoading: isLoadingTransaction,
      isSuccess: isSuccessTransaction
    } = useTransactionsGetQuery(id);
    const {
      data: portfolio,
      isLoading: isLoadingPortfolio,
      isSuccess: isSuccessPortfolio
    } = usePortfoliosGetQuery(transaction?.portfolioId ?? '', {
      enabled: !!transaction?.portfolioId
    });

    const { mutate: approveTransaction, isPending: approveTransactionPending } =
      useTransactionsApproveMutation(id, {
        onSuccess: () => {
          queryClient.invalidateQueries();
          toast.success(t('::Transaction:Created'));
          onComplete();
        },
        onError: () => {}
      });

    const { mutate: deleteNotification, isPending: deleteNotificationPending } =
      useNotificationsDeleteMutation(notificationId, {
        onSuccess: () => {
          invalidateNotificationsGetListQuery(queryClient);
          onComplete();
        }
      });

    if (isLoadingTransaction) {
      return <Loader>{t('::Transaction:Loading')}</Loader>;
    }

    if (isSuccessTransaction) {
      const type = transactionTypeOptions.find(option => option.value === transaction.type);
      const syncDate = transaction.date;
      const transactionDate = transaction.extraProperties[externalTransactionDate];

      return (
        <Column my={'$3'}>
          <Row justifyContent="space-between">
            <Paragraph size={'$7'} fontWeight={'$7'}>
              {type ? t(type.key) : null}{' '}
              {formatCurrency(Math.abs(transaction.amount.value), transaction.amount.currency)}
            </Paragraph>
          </Row>
          <Paragraph>{transaction.comment}</Paragraph>
          <Row justifyContent="space-between">
            <Paragraph>{t('::Portfolio')}</Paragraph>
            {isSuccessPortfolio ? <Paragraph>{portfolio.name}</Paragraph> : null}
            {isLoadingPortfolio ? <Spinner /> : null}
          </Row>
          {transactionDate ? (
            <Row justifyContent="space-between">
              <Paragraph>{t('::Transaction:Date')}</Paragraph>
              <Paragraph>{formatDate(transactionDate)}</Paragraph>
            </Row>
          ) : null}
          <Row justifyContent="space-between">
            <Paragraph>{t('::Transaction:DateImported')}</Paragraph>
            <Paragraph>{formatDate(syncDate)}</Paragraph>
          </Row>
          {transaction.status === TransactionStatus.PendingApproval ? (
            <Row mt={'$3'}>
              <Button
                withFeedback
                theme={'red'}
                variant={'outlined'}
                size={'$3'}
                flex={1}
                disabled={approveTransactionPending || deleteNotificationPending}
                onPress={() => deleteNotification()}
              >
                <Button.Icon>
                  <Trash2 />
                </Button.Icon>
                <Button.Text>{t('::Transaction:Disapprove')}</Button.Text>
                {deleteNotificationPending ? <Button.Spinner /> : null}
              </Button>
              <Button
                withFeedback
                size={'$3'}
                flex={1}
                disabled={approveTransactionPending || deleteNotificationPending}
                onPress={() => approveTransaction()}
              >
                <Button.Icon>
                  <Check />
                </Button.Icon>
                <Button.Text>{t('::Transaction:Approve')}</Button.Text>
                {approveTransactionPending ? <Button.Spinner /> : null}
              </Button>
            </Row>
          ) : null}
        </Column>
      );
    }

    return (
      <NoData Icon={CircleX} color={'$danger'} text={t('::Transaction:NotFound')} size={'$6'} />
    );
  }
);

TransactionNotificationData.displayName = 'TransactionNotificationData';
